import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addBookmark, removeBookmark, isArticleBookmarked } from "@/services/bookmarkService";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/hooks/use-toast";

// Optimistic bookmark toggle. Flips cache immediately, rolls back on error.
export function useBookmark(articleId: string | undefined) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { toast } = useToast();
  const key = ["bookmark", user?.id, articleId];

  const { data: isBookmarked = false } = useQuery({
    queryKey: key,
    queryFn: () => (user && articleId ? isArticleBookmarked(user.id, articleId) : false),
    enabled: !!user && !!articleId,
    staleTime: 60_000,
  });

  const mutation = useMutation({
    mutationFn: async (next: boolean) => {
      if (!user || !articleId) throw new Error("Not signed in");
      if (next) await addBookmark(user.id, articleId);
      else await removeBookmark(user.id, articleId);
      return next;
    },
    onMutate: async (next: boolean) => {
      await qc.cancelQueries({ queryKey: key });
      const prev = qc.getQueryData<boolean>(key);
      qc.setQueryData(key, next);
      return { prev };
    },
    onError: (_err, _next, ctx) => {
      if (ctx?.prev !== undefined) qc.setQueryData(key, ctx.prev);
      toast({ variant: "destructive", title: "Bookmark failed", description: "Please try again." });
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ["bookmarks", user?.id] });
    },
  });

  return {
    isBookmarked,
    toggle: () => mutation.mutate(!isBookmarked),
    setBookmarked: (v: boolean) => mutation.mutate(v),
    isPending: mutation.isPending,
  };
}
